import React, { createContext, useState, useContext, useMemo } from 'react'

import { useMutation } from '@apollo/client'
import { get, set, remove } from 'local-storage'

import { useFilters } from './filterContext'
import { useUser } from './userContext'

import { useHasRoles } from '@/hooks/useHasRoles'
import { Role } from '@/modules/adminUsers/roles'
import { SWITCH_SUPER_ADMIN_BUILDER } from '@/modules/builder/graphQL'

import {
  SwitchSuperAdminBuilderUiMutation,
  SwitchSuperAdminBuilderUiMutationVariables,
} from 'types/api.graphql'

type OriginalBuilder = {
  id: string
  name: string
}

type SupportContextType = {
  isInSupportMode: boolean
  originalBuilder: OriginalBuilder
  setOriginalBuilder: (builder: OriginalBuilder) => void
  clearOriginalBuilder: () => void
  resetToOriginalBuilder: () => void
  switchUsersBuilder: (builderId: string) => void
  showBanner: boolean
  toggleBanner: (show: boolean) => void
}

const defaultStore = {
  isInSupportMode: false,
  originalBuilder: {
    id: '',
    name: '',
  },
  setOriginalBuilder: () => {},
  clearOriginalBuilder: () => {},
  resetToOriginalBuilder: () => {},
  switchUsersBuilder: () => {},
  showBanner: true,
  toggleBanner: () => {},
}

export const SupportContext = createContext<SupportContextType>(defaultStore)

export const LS_SUPPORT_KEY = 'buildpass-support-access'
export const LS_SHOW_BANNER_KEY = 'buildpass-support-banner'

const getBuilderFromLocalStorage = () => get<OriginalBuilder>(LS_SUPPORT_KEY)
const getShowBannerFromLocalStorage = () => get<boolean>(LS_SHOW_BANNER_KEY)

export const updateBuilderInLocalStorage = (
  originalBuilder: OriginalBuilder
) => {
  set(LS_SUPPORT_KEY, originalBuilder)

  return originalBuilder
}

export const SupportProvider = ({ children }) => {
  const { clearAllFilters } = useFilters()
  const { builder } = useUser()

  const isSuperAdmin = useHasRoles(Role.SuperAdmin)
  const storedBuilder = useMemo(getBuilderFromLocalStorage, [])
  const storedShowBanner = useMemo(getShowBannerFromLocalStorage, [])

  const [switchUsersBuilderMutation] = useMutation<
    SwitchSuperAdminBuilderUiMutation,
    SwitchSuperAdminBuilderUiMutationVariables
  >(SWITCH_SUPER_ADMIN_BUILDER)

  const [builderStore, setBuilderStore] = useState<OriginalBuilder>(
    storedBuilder || defaultStore.originalBuilder
  )

  const [showBanner, setShowBanner] = useState<boolean>(
    storedShowBanner ?? true
  )

  const toggleBanner = (show: boolean) => {
    setShowBanner(show)
    set(LS_SHOW_BANNER_KEY, show)
  }

  const isInSupportMode = useMemo(
    () => isSuperAdmin && builderStore.id && builder?.id !== builderStore?.id,
    [builder, builderStore, isSuperAdmin]
  )

  const resetApplication = () => {
    clearAllFilters()
  }

  const setOriginalBuilder = (builder: OriginalBuilder) => {
    const builderClone = updateBuilderInLocalStorage(builder)
    setBuilderStore(builderClone)
    toggleBanner(true)
  }

  const clearOriginalBuilder = () => {
    setBuilderStore(defaultStore.originalBuilder)
    remove(LS_SUPPORT_KEY)
  }

  const switchUsersBuilder = async (builderId: string) => {
    const noExistingBuilderCached = builderStore?.id === '' || !builderStore

    if (noExistingBuilderCached) {
      // set local storage
      setOriginalBuilder({
        id: builder?.id,
        name: builder?.company?.name,
      })
    }

    await switchUsersBuilderMutation({
      variables: {
        builderId,
      },
    })

    resetApplication()

    toggleBanner(true)

    // reload page
    window.location.reload()
  }

  const resetToOriginalBuilder = async () => {
    if (isInSupportMode) {
      await switchUsersBuilderMutation({
        variables: { builderId: builderStore.id },
      })

      resetApplication()

      // reload page
      window.location.reload()
    }
  }

  return (
    <SupportContext.Provider
      value={{
        showBanner,
        toggleBanner,
        setOriginalBuilder,
        clearOriginalBuilder,
        originalBuilder: isSuperAdmin
          ? builderStore
          : defaultStore.originalBuilder,
        isInSupportMode,
        resetToOriginalBuilder,
        switchUsersBuilder,
      }}
    >
      {children}
    </SupportContext.Provider>
  )
}

export const useSupport = () => useContext(SupportContext)
